import { userActions } from '@/actions';
import { Button } from '@/components/elements/forms/buttons';
import { Switch } from '@/components/elements/forms/Switch';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { LoadingIcon, WarningIcon } from '@/components/icons';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { HomepageAdminUser, HomepageSection, homepageServices } from '@/services';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

const GridIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const UsersIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SignOutIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const DragHandleIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

type AudienceTab = 'logged_in' | 'logged_out';

const AUDIENCE_TAB_LABELS: Record<AudienceTab, string> = {
  logged_in: 'Logged in',
  logged_out: 'Guests',
};

/** Sorts section types by their order field for the given audience (missing order goes last). */
const sortTypesByOrder = (sections: HomepageSection[], orderField: 'orderLoggedIn' | 'orderLoggedOut'): string[] =>
  [...sections]
    .sort((a, b) => {
      const ao = a[orderField];
      const bo = b[orderField];
      if (ao === null && bo === null) return 0;
      if (ao === null) return 1;
      if (bo === null) return -1;
      return ao - bo;
    })
    .map((s) => s.type);

type View = 'sections' | 'users';

type RoleValue = '2' | '3' | 'null';

const ROLE_SELECT_OPTIONS: { value: RoleValue; label: string }[] = [
  { value: '2', label: 'Manager' },
  { value: '3', label: 'Admin' },
  { value: 'null', label: 'None (remove)' },
];

const roleValueOf = (isAdmin: number | null | undefined): RoleValue => {
  if (isAdmin === 3) return '3';
  if (isAdmin === 2) return '2';
  return 'null';
};

const parseRoleValue = (v: RoleValue): number | null => (v === 'null' ? null : parseInt(v, 10));

const roleLabelOf = (isAdmin: number | null | undefined): string => {
  if (isAdmin === 3) return 'Admin';
  if (isAdmin === 2) return 'Manager';
  if (isAdmin === 1) return 'Support';
  return 'None';
};

const RoleControl = ({
  user,
  canEdit,
  isSelf,
  onRoleChange,
}: {
  user: HomepageAdminUser;
  canEdit: boolean;
  isSelf: boolean;
  onRoleChange: (id: number, isAdmin: number | null) => void;
}) => {
  if (!canEdit) {
    return <span className="text-black-700 text-sm font-medium">{roleLabelOf(user.isAdmin)}</span>;
  }
  return (
    <select
      disabled={isSelf}
      className="border-black-200 disabled:bg-black-50 disabled:text-black-500 rounded border bg-white px-2 py-1 text-sm disabled:cursor-not-allowed"
      value={roleValueOf(user.isAdmin)}
      onChange={(e) => onRoleChange(user.id, parseRoleValue(e.target.value as RoleValue))}>
      {ROLE_SELECT_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
};

const UserRow = ({
  user,
  canEdit,
  isSelf,
  onRoleChange,
}: {
  user: HomepageAdminUser;
  canEdit: boolean;
  isSelf: boolean;
  onRoleChange: (id: number, isAdmin: number | null) => void;
}) => (
  <div className="grid grid-cols-[1fr_1.5fr_10rem] items-center gap-4">
    <span className="text-black-900 truncate text-sm font-medium">{user.name || '—'}</span>
    <span className="text-black-600 truncate text-sm">{user.email}</span>
    <RoleControl user={user} canEdit={canEdit} isSelf={isSelf} onRoleChange={onRoleChange} />
  </div>
);

function displayToast(success: boolean, label: string) {
  toast(
    ({ closeToast }) => (
      <Snackbar
        label={label}
        type={success ? 'success' : 'danger'}
        action={<button onClick={closeToast}>{success ? 'OK' : 'Close'}</button>}
      />
    ),
    { autoClose: 3000 },
  );
}

const HomepageSupport = () => {
  const [view, setView] = useState<View>('sections');

  const [sectionsByType, setSectionsByType] = useState<Record<string, HomepageSection>>({});
  const [loggedInOrder, setLoggedInOrder] = useState<string[]>([]);
  const [loggedOutOrder, setLoggedOutOrder] = useState<string[]>([]);
  const [audienceTab, setAudienceTab] = useState<AudienceTab>('logged_in');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragStartIndex = useRef<number | null>(null);

  const [users, setUsers] = useState<HomepageAdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [canEditRoles, setCanEditRoles] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState<HomepageAdminUser | null>(null);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [searching, setSearching] = useState(false);

  const dispatch = useAppDispatch();
  const userEmail = useAppSelector((state) => state?.users?.user?.contact?.email);

  useEffect(() => {
    homepageServices
      .getSections()
      .then((data) => {
        const loaded = data?.sections ?? [];
        const byType: Record<string, HomepageSection> = {};
        loaded.forEach((s) => {
          byType[s.type] = s;
        });
        setSectionsByType(byType);
        setLoggedInOrder(sortTypesByOrder(loaded, 'orderLoggedIn'));
        setLoggedOutOrder(sortTypesByOrder(loaded, 'orderLoggedOut'));
        setError('');
      })
      .catch(() => {
        setError('Failed to load homepage sections. Make sure you have admin access.');
      })
      .finally(() => setLoading(false));
  }, []);

  const currentOrder = audienceTab === 'logged_in' ? loggedInOrder : loggedOutOrder;
  const setCurrentOrder = audienceTab === 'logged_in' ? setLoggedInOrder : setLoggedOutOrder;
  const visibleField = audienceTab === 'logged_in' ? 'visibleLoggedIn' : 'visibleLoggedOut';

  const handleDragStart = (index: number) => {
    dragStartIndex.current = index;
    setDragIndex(index);
  };

  const handleDragEnter = (index: number) => {
    setDragIndex((current) => {
      if (current === null || current === index) return current;
      setCurrentOrder((prev) => {
        const next = [...prev];
        const [moved] = next.splice(current, 1);
        next.splice(index, 0, moved);
        return next;
      });
      return index;
    });
  };

  const handleDragEnd = () => {
    const start = dragStartIndex.current;
    const end = dragIndex;

    if (start !== null && end !== null && start !== end) {
      setIsDirty(true);
    }

    dragStartIndex.current = null;
    setDragIndex(null);
  };

  const handleToggleVisible = (type: string) => {
    setSectionsByType((prev) => ({
      ...prev,
      [type]: { ...prev[type], [visibleField]: !prev[type][visibleField] },
    }));
    setIsDirty(true);
  };

  const handleSave = () => {
    setSaving(true);
    const sections: HomepageSection[] = Object.keys(sectionsByType).map((type) => ({
      ...sectionsByType[type],
      orderLoggedIn: loggedInOrder.indexOf(type) + 1,
      orderLoggedOut: loggedOutOrder.indexOf(type) + 1,
    }));
    homepageServices
      .updateSections(sections)
      .then((data) => {
        const saved = data?.sections ?? sections;
        const byType: Record<string, HomepageSection> = {};
        saved.forEach((s) => {
          byType[s.type] = s;
        });
        setSectionsByType(byType);
        setLoggedInOrder(sortTypesByOrder(saved, 'orderLoggedIn'));
        setLoggedOutOrder(sortTypesByOrder(saved, 'orderLoggedOut'));
        setIsDirty(false);
        displayToast(true, 'Homepage sections saved successfully');
      })
      .catch(() => {
        displayToast(false, 'Failed to save homepage sections');
      })
      .finally(() => setSaving(false));
  };

  const handleSignOut = () => {
    dispatch(userActions.logout());
  };

  const loadUsers = () => {
    setUsersLoading(true);
    setUsersError('');
    homepageServices
      .getAdminUsers()
      .then((data) => {
        setUsers(data?.users ?? []);
        setCanEditRoles(!!data?.canEditRoles);
      })
      .catch(() => setUsersError('Failed to load admin users. Make sure you have admin access.'))
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    if (view === 'users' && users.length === 0 && !usersLoading && !usersError) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const email = searchEmail.trim();
    if (!email) return;
    setSearching(true);
    setSearchedOnce(true);
    homepageServices
      .findUserByEmail(email)
      .then((data: any) => {
        if (data && data.id) {
          setSearchResult(data as HomepageAdminUser);
        } else {
          setSearchResult(null);
        }
      })
      .catch(() => setSearchResult(null))
      .finally(() => setSearching(false));
  };

  const handleRoleChange = (id: number, isAdmin: number | null) => {
    homepageServices
      .setUserRole(id, isAdmin)
      .then(() => {
        displayToast(true, 'Role updated');
        loadUsers();
        if (searchResult && searchResult.id === id) {
          setSearchResult({ ...searchResult, isAdmin });
        }
      })
      .catch(() => displayToast(false, 'Failed to update role'));
  };

  const visibleCount = currentOrder.filter((type) => sectionsByType[type]?.[visibleField]).length;

  return (
    <div className="bg-black-50 flex min-h-screen">
      {/* Sidebar */}
      <aside className="bg-black-900 flex w-[200px] flex-col text-white">
        <div className="border-black-800 border-b px-5 py-5">
          <div className="text-lg font-bold leading-tight">Bokadirekt</div>
          <div className="text-black-400 mt-1 text-xs font-medium tracking-widest">ADMIN</div>
        </div>
        <nav className="flex-1 px-3 py-4">
          <button
            type="button"
            onClick={() => setView('sections')}
            className={`mb-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${
              view === 'sections'
                ? 'bg-black-800 font-medium text-white'
                : 'text-black-300 hover:bg-black-800'
            }`}>
            <GridIcon />
            Home Sections
          </button>
          <button
            type="button"
            onClick={() => setView('users')}
            className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${
              view === 'users' ? 'bg-black-800 font-medium text-white' : 'text-black-300 hover:bg-black-800'
            }`}>
            <UsersIcon />
            Admin Users
          </button>
        </nav>
        <div className="border-black-800 border-t p-3">
          {userEmail && (
            <>
              <div className="text-black-300 mb-2 truncate px-2 text-xs">{userEmail}</div>
              <button
                type="button"
                onClick={handleSignOut}
                className="bg-black-800 hover:bg-black-700 flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm">
                <SignOutIcon />
                Sign out
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8">
        {view === 'sections' && (
          <>
            <header className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-black-900 text-2xl font-bold">Home Sections</h1>
            <p className="text-black-500 mt-1 text-sm">
              {visibleCount} of {currentOrder.length} section{currentOrder.length !== 1 ? 's' : ''} visible · Drag to
              reorder
              {isDirty && <span className="text-warning-700 ml-2">• Unsaved changes</span>}
            </p>
          </div>
          <Button variant="tertiary" size="lg" disabled={saving || !isDirty} onClick={handleSave}>
            {saving && <LoadingIcon className="mr-2 h-4 w-4" />}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </header>

        <div className="border-black-100 mb-6 inline-flex rounded-full border bg-white p-1">
          {(['logged_in', 'logged_out'] as AudienceTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setAudienceTab(tab)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                audienceTab === tab ? 'bg-black-900 text-white' : 'text-black-500 hover:bg-black-50'
              }`}>
              {AUDIENCE_TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center gap-2 py-8">
            <LoadingIcon className="text-black-500 h-5 w-5" />
            <span>Loading sections…</span>
          </div>
        )}

        {!!error && !loading && (
          <div className="mb-6 flex w-full items-center gap-3 rounded-lg bg-white p-6">
            <WarningIcon className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-3">
            {currentOrder.map((type, index) => {
              const section = sectionsByType[type];
              if (!section) return null;
              const isVisible = section[visibleField];

              return (
                <div
                  key={type}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  className={`border-black-100 grid cursor-grab grid-cols-[1.5rem_2rem_1fr_3rem] items-center gap-4 rounded-full border px-5 py-3 transition-opacity active:cursor-grabbing ${
                    dragIndex === index ? 'opacity-30' : !isVisible ? 'bg-black-50 opacity-60' : 'bg-white'
                  }`}>
                  <span className="text-black-300">
                    <DragHandleIcon className="h-5 w-5" />
                  </span>

                  <span className="bg-black-50 text-black-700 inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium">
                    {index + 1}
                  </span>

                  <div className="flex min-w-0 flex-col">
                    <span className="text-black-900 truncate font-semibold">{section.title}</span>
                    <span className="text-black-400 truncate text-xs uppercase tracking-wider">{section.type}</span>
                  </div>

                  <div className="flex justify-end">
                    <Switch switched={isVisible} onChange={() => handleToggleVisible(type)} alt={section.title} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </>
        )}

        {view === 'users' && (
          <>
            <header className="mb-6">
              <h1 className="text-black-900 text-2xl font-bold">Admin Users</h1>
            </header>

            {canEditRoles && (
              <form onSubmit={handleSearch} className="mb-6 flex items-center gap-2">
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  placeholder="Search user by email…"
                  className="border-black-200 flex-1 rounded-full border bg-white px-4 py-2 text-sm focus:outline-none"
                />
                <Button variant="tertiary" size="lg" disabled={searching || !searchEmail.trim()}>
                  {searching ? 'Searching…' : 'Search'}
                </Button>
              </form>
            )}

            {canEditRoles && searchedOnce && !searching && !searchResult && (
              <div className="mb-6 rounded-lg bg-white p-4 text-sm text-black-500">No user found with that email.</div>
            )}

            {searchResult && (
              <div className="border-black-200 mb-6 rounded-lg border bg-white p-4">
                <div className="text-black-500 mb-2 text-xs font-medium uppercase tracking-wider">Search result</div>
                <UserRow
                  user={searchResult}
                  canEdit={canEditRoles}
                  isSelf={!!userEmail && searchResult.email === userEmail}
                  onRoleChange={handleRoleChange}
                />
              </div>
            )}

            {usersLoading && (
              <div className="flex items-center gap-2 py-8">
                <LoadingIcon className="text-black-500 h-5 w-5" />
                <span>Loading users…</span>
              </div>
            )}

            {!!usersError && !usersLoading && (
              <div className="mb-6 flex w-full items-center gap-3 rounded-lg bg-white p-6">
                <WarningIcon className="h-5 w-5 flex-shrink-0" />
                <span>{usersError}</span>
              </div>
            )}

            {!usersLoading && !usersError && (
              <div className="border-black-200 overflow-hidden rounded-lg border bg-white">
                <div className="bg-black-50 border-black-200 text-black-600 grid grid-cols-[1fr_1.5fr_10rem] gap-4 border-b px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                  <span>Name</span>
                  <span>Email</span>
                  <span>Role</span>
                </div>
                {users.length === 0 && (
                  <div className="text-black-500 px-5 py-6 text-sm">No managers or admins yet.</div>
                )}
                {users.map((u) => (
                  <div key={u.id} className="border-black-100 grid grid-cols-[1fr_1.5fr_10rem] items-center gap-4 border-b px-5 py-3 last:border-b-0">
                    <span className="text-black-900 truncate text-sm font-medium">{u.name || '—'}</span>
                    <span className="text-black-600 truncate text-sm">{u.email}</span>
                    <RoleControl
                      user={u}
                      canEdit={canEditRoles}
                      isSelf={!!userEmail && u.email === userEmail}
                      onRoleChange={handleRoleChange}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default HomepageSupport;
